import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isIOS } from '../lib/display.ts';
import { canPromptInstall, isInstalled, onInstallChange, promptInstall } from '../lib/install.ts';
import { getPosition, locationPermission } from '../lib/places.ts';
import { enablePush, getPushState } from '../lib/push.ts';
import { IconBell, IconPin, IconPlus } from './icons.tsx';
import { IosInstallSteps } from './InstallCoach.tsx';
import { Sheet, Spinner } from './ui.tsx';

type Step = 'push' | 'location' | 'install' | 'install-ios';

const SNOOZE_MS = 7 * 86_400_000;
const key = (step: Step) => `klarnus.onboarding.${step}`;

/** "Non ora" on one request snoozes that request only, for a week; the others still get asked. */
export function snoozed(step: Step, now = Date.now()): boolean {
  try {
    return Number(localStorage.getItem(key(step)) ?? 0) > now;
  } catch {
    return false;
  }
}

function snooze(step: Step): void {
  try {
    localStorage.setItem(key(step), String(Date.now() + SNOOZE_MS));
  } catch {
    // Storage blocked: the sheet may come back next launch, which is the lesser evil.
  }
}

/** Asked once and answered: remembered, because Safari may not report the permission state. */
const LOCATION_ASKED = 'klarnus.onboarding.location.answered';
const locationAnswered = () => {
  try {
    return localStorage.getItem(LOCATION_ASKED) === '1';
  } catch {
    return false;
  }
};

/**
 * The next thing to ask, in order: notifications, location, then — optional — installing.
 * Notifications come first because they are the one the browser lets the app ask only once.
 * Nothing here requires installing: Android and computers grant push in the browser tab, and
 * location works everywhere. iPhone in Safari is the exception for push only (Apple offers web
 * push solely to Home Screen apps), so there the push request is skipped, not faked.
 */
export async function nextStep(): Promise<Step | null> {
  if (!snoozed('push') && (await getPushState()) === 'default') return 'push';
  if (!snoozed('location') && !locationAnswered()) {
    const state = await locationPermission();
    if (state === 'prompt' || state === 'unknown') return 'location';
  }
  if (!isInstalled()) {
    if (canPromptInstall() && !snoozed('install')) return 'install';
    if (isIOS() && !snoozed('install-ios')) return 'install-ios';
  }
  return null;
}

/**
 * What the app asks for as soon as you are in: notifications and location, then installing.
 *
 * None of it can be asked silently. Browsers ignore — and Chrome penalises — a notification
 * prompt that is not the answer to a tap, and iOS may ignore a location request that is not. So
 * each request is the app's own sheet, and the browser's prompt follows the user's tap on it.
 */
export function Onboarding() {
  const location = useLocation();
  const [step, setStep] = useState<Step | null>(null);
  const [busy, setBusy] = useState(false);

  const advance = () => void nextStep().then(setStep);

  useEffect(() => {
    if (location.pathname !== '/') return;
    // A moment after the cards are on screen, never before them.
    const t = setTimeout(advance, 900);
    const off = onInstallChange(advance);
    return () => {
      clearTimeout(t);
      off();
    };
  }, [location.pathname]);

  const later = (s: Step) => () => {
    snooze(s);
    advance();
  };

  if (step === 'push') {
    return (
      <Sheet open onClose={later('push')} title="Attiva le notifiche">
        <span className="sheet-art"><IconBell size={28} /></span>
        <p className="muted" style={{ fontSize: 15.5 }}>
          Ti avvisiamo quando una carta sta per scadere e quando Klarnus ha qualcosa di nuovo. Nient’altro.
        </p>
        <button
          className="btn block" disabled={busy}
          // Straight out of the tap: a deferred request is ignored on iOS and penalised by Chrome.
          onClick={() => {
            setBusy(true);
            void enablePush().catch(() => {}).finally(() => {
              setBusy(false);
              snooze('push'); // answered either way; the browser remembers a refusal by itself
              advance();
            });
          }}
        >
          {busy ? <Spinner /> : 'Attiva le notifiche'}
        </button>
        <button className="btn ghost block" onClick={later('push')}>Non ora</button>
      </Sheet>
    );
  }

  if (step === 'location') {
    return (
      <Sheet open onClose={later('location')} title="Usa la tua posizione">
        <span className="sheet-art"><IconPin size={28} /></span>
        <p className="muted" style={{ fontSize: 15.5 }}>
          Quando apri Klarnus in un negozio dove hai già usato una carta, te la mostriamo subito, senza cercarla.
          La posizione resta tua: la usiamo solo quando l’app è aperta.
        </p>
        <button
          className="btn block" disabled={busy}
          onClick={() => {
            setBusy(true);
            // The request itself is what shows the browser's permission prompt.
            void getPosition({ timeout: 15_000 }).catch(() => {}).finally(() => {
              try { localStorage.setItem(LOCATION_ASKED, '1'); } catch { /* see snooze */ }
              setBusy(false);
              advance();
            });
          }}
        >
          {busy ? <Spinner /> : 'Consenti la posizione'}
        </button>
        <button className="btn ghost block" onClick={later('location')}>Non ora</button>
      </Sheet>
    );
  }

  if (step === 'install') {
    return (
      <Sheet open onClose={later('install')} title="Installa Klarnus">
        <span className="sheet-art"><IconPlus size={28} /></span>
        <p className="muted" style={{ fontSize: 15.5 }}>Si apre dalla sua icona come un’app, a tutto schermo, anche senza rete.</p>
        <button className="btn block" onClick={() => void promptInstall().finally(advance)}>Installa</button>
        <button className="btn ghost block" onClick={later('install')}>Non ora</button>
      </Sheet>
    );
  }

  if (step === 'install-ios') {
    return (
      <Sheet open onClose={later('install-ios')} title="Mettila sulla schermata Home">
        <p className="muted" style={{ fontSize: 15.5 }}>
          Funziona già così. Dalla Home si apre come un’app e, su iPhone, può anche avvisarti quando una carta scade.
        </p>
        <IosInstallSteps />
        <button className="btn block" onClick={later('install-ios')}>Ho capito</button>
      </Sheet>
    );
  }

  return null;
}
